import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, Tabs, Tab, Typography, FormControl, InputLabel, 
  Select, MenuItem, Button, Paper, Alert, Snackbar, TextField,
  CircularProgress 
} from '@mui/material';
import { useAuth } from '../components/AuthContext';
import { API_BASE_URL } from '../components/api.js';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

const MESES_NOMES = [
  'Janeiro','Fevereiro','Março','Abril','Maio','Junho',
  'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'
];

function PontoEditar() {
  const [tabIndex, setTabIndex] = useState(0);
  const { authenticatedFetch } = useAuth();
  
  // Estados para Listagem/Exclusão
  const [editar_opcoesAnos, setEditar_opcoesAnos] = useState([]);
  const [editar_opcoesMeses, setEditar_opcoesMeses] = useState([]);
  const [editar_ano, setEditar_ano] = useState('');
  const [editar_mes, setEditar_mes] = useState('');
  
  // Estados para Geração de Novo Ponto
  const [editar_anoNovo, setEditar_anoNovo] = useState(new Date().getFullYear());
  const [editar_mesNovo, setEditar_mesNovo] = useState(new Date().getMonth() + 1);
  const [gerando, setGerando] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // --- CARGA DE ANOS (Para a Tab de Exclusão) ---
  const fetchAnosEditar = useCallback(async () => {
    try {
      const res = await authenticatedFetch(`${API_BASE_URL}/api/ponto-buscar-anos/`);
      if (res.ok) {
        const anos = await res.json();
        setEditar_opcoesAnos(anos);
        if (anos.length > 0 && !editar_ano) {
           setEditar_ano(anos[0]);
        }
      }
    } catch (err) { console.error("Erro ao buscar anos:", err); }
  }, [authenticatedFetch, editar_ano]);

  // --- CARGA DE MESES (Para a Tab de Exclusão) ---
  const fetchMesesEditar = useCallback(async (anoAlvo) => {
    if (!anoAlvo) return;
    try {
      const res = await authenticatedFetch(`${API_BASE_URL}/api/ponto-buscar-meses/?ano=${anoAlvo}`);
      if (res.ok) {
        const meses = await res.json();
        setEditar_opcoesMeses(meses);
        if (meses.length > 0) setEditar_mes(meses[0]);
      }
    } catch (err) { console.error("Erro ao buscar meses:", err); }
  }, [authenticatedFetch]);

  useEffect(() => { fetchAnosEditar(); }, [fetchAnosEditar]);

  useEffect(() => {
    if (editar_ano) fetchMesesEditar(editar_ano);
  }, [editar_ano, fetchMesesEditar]);

  // --- FUNÇÃO PARA GERAR NOVO PONTO (Tradução da lógica C#) ---
  const handleGerarPonto = async () => {
    if (!editar_anoNovo || !editar_mesNovo) {
        setSnackbar({ open: true, message: 'Informe ano e mês válidos.', severity: 'warning' });
        return;
    }

    setGerando(true);
    try {
        const res = await authenticatedFetch(`${API_BASE_URL}/api/ponto-editar-adicionar-mes/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ano: editar_anoNovo, mes: editar_mesNovo }),
        });

        const data = await res.json();

        if (res.ok) {
            const { criados, ja_existiam } = data;
            const mesStr = String(editar_mesNovo).padStart(2, '0');
            const anoStr = editar_anoNovo;

            let mensagem = '';
            let severity = 'success';

            if (criados > 0 && ja_existiam === 0) {
                mensagem = `Ponto de ${mesStr}/${anoStr} gerado com sucesso. ${criados} registros criados.`;
            } else if (criados > 0 && ja_existiam > 0) {
                mensagem = `Ponto de ${mesStr}/${anoStr} atualizado. ${criados} registros incluídos, ${ja_existiam} já existiam.`;
            } else {
                mensagem = `Ponto de ${mesStr}/${anoStr} já existia, nada foi alterado.`;
                severity = 'info';
            }

            setSnackbar({ open: true, message: mensagem, severity });
            fetchAnosEditar();
        } else {
            setSnackbar({ open: true, message: data.erro || 'Erro ao gerar registros.', severity: 'error' });
        }
    } catch (err) {
        setSnackbar({ open: true, message: 'Erro ao conectar com o servidor.', severity: 'error' });
    } finally {
        setGerando(false);
    }
  };

  // --- FUNÇÃO PARA EXCLUIR MÊS ---
  const handleExcluir = async () => {
    if (!window.confirm(`ATENÇÃO: Deseja realmente excluir todos os registros de ${editar_mes}/${editar_ano}? Esta ação é irreversível.`)) return;

    try {
      const res = await authenticatedFetch(
        `${API_BASE_URL}/api/ponto-excluir-mes/?ano=${editar_ano}&mes=${editar_mes}`, 
        { method: 'DELETE' }
      );
      const data = await res.json();

      if (res.ok) {
        setSnackbar({ open: true, message: data.mensagem, severity: 'success' });
        setEditar_ano('');
        setEditar_mes('');
        fetchAnosEditar();
      } else {
        setSnackbar({ open: true, message: data.erro, severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Erro ao excluir registros.', severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>Configurações do Ponto</Typography>
      
      <Tabs value={tabIndex} onChange={(e, v) => setTabIndex(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Adicionar Mês (Gerar)" />
        <Tab label="Excluir Mês" />
      </Tabs>

      {/* ABA 0: ADICIONAR / GERAR */}
      <TabPanel value={tabIndex} index={0}>
        <Paper variant="outlined" sx={{ p: 3, maxWidth: 500 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, color: 'text.secondary' }}>
            Esta ação irá gerar automaticamente os horários de entrada e saída para todos os funcionários ativos baseados em suas escalas.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField 
              label="Ano" 
              type="number" 
              size="small" 
              value={editar_anoNovo} 
              onChange={e => setEditar_anoNovo(e.target.value)} 
            />
            <TextField 
              label="Mês" 
              type="number" 
              size="small" 
              inputProps={{ min: 1, max: 12 }}
              value={editar_mesNovo} 
              onChange={e => setEditar_mesNovo(e.target.value)} 
            />
          </Box>

          <Button 
            variant="contained" 
            fullWidth 
            sx={{ maxWidth: '200px', height: '45px' }}
            onClick={handleGerarPonto}
            disabled={gerando}
          >
            {gerando ? <CircularProgress size={24} color="inherit" /> : 'Gerar Novo Ponto'}
          </Button>
        </Paper>
      </TabPanel>

      {/* ABA 1: EXCLUIR */}
      <TabPanel value={tabIndex} index={1}>
        <Paper variant="outlined" sx={{ p: 3, bgcolor: '#fdfdfd' }}>
          <Typography variant="subtitle1" sx={{ mb: 2, color: 'error.main' }}>
            Selecione um período para remover todos os registros de ponto do banco de dados.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <FormControl sx={{ minWidth: 120 }} size="small">
              <InputLabel>Ano</InputLabel>
              <Select 
                value={editar_ano || ''} 
                label="Ano" 
                onChange={(e) => {
                  setEditar_ano(e.target.value);
                  setEditar_mes('');
                }}
              >
                {editar_opcoesAnos.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 200 }} size="small" disabled={!editar_ano}>
              <InputLabel>Mês</InputLabel>
              <Select 
                value={editar_mes || ''} 
                label="Mês" 
                onChange={(e) => setEditar_mes(e.target.value)}
              >
                {editar_opcoesMeses.map(m => (
                  <MenuItem key={m} value={m}>
                    {String(m).padStart(2, '0')} - {MESES_NOMES[m - 1]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Button 
            variant="contained" 
            color="error" 
            disabled={!editar_mes}
            onClick={handleExcluir}
          >
            Excluir Registros Selecionados
          </Button>
        </Paper>
      </TabPanel>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={() => setSnackbar({...snackbar, open: false})}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PontoEditar;